import { useCallback, useMemo } from "react";
import { getViewerActions, getViewerState } from "../../stores/viewerStore";

export function useSelectedLayerVisibilityNotice({
  collisionGeomVisibility,
  equalityMarkerVisibility,
  inertialVisibility,
  jointVizVisibility,
  safeBodies,
  selectedEquality,
  selectedGeom,
  selectedInertial,
  selectedJoint,
  selectedSite,
  selectedTendon,
  showCollision,
  showConstraintMarkers,
  showInertials,
  showJoints,
  showSites,
  showVisual,
  siteVisibility,
  tendonMarkerVisibility,
}) {
  const notice = useMemo(() => {
    if (selectedJoint && !showJoints && (jointVizVisibility[selectedJoint] ?? true) !== false) {
      return {
        message: "Joint visible while selected.",
        visibilityLabel: "Joint",
        isVisibilityOn: () => getViewerState().showJoints,
        turnVisibilityOn: () => getViewerActions().toggleShowJoints(),
      };
    }
    if (selectedSite && !showSites && (siteVisibility[selectedSite] ?? true) !== false) {
      return {
        message: "Site visible while selected.",
        visibilityLabel: "Site",
        isVisibilityOn: () => getViewerState().showSites,
        turnVisibilityOn: () => getViewerActions().toggleShowSites(),
      };
    }
    if (
      selectedInertial &&
      !showInertials &&
      (inertialVisibility[selectedInertial] ?? true) !== false
    ) {
      return {
        message: "Inertial visible while selected.",
        visibilityLabel: "Inertial",
        isVisibilityOn: () => getViewerState().showInertials,
        turnVisibilityOn: () => getViewerActions().toggleShowInertials(),
      };
    }
    if (selectedGeom) {
      const selectedGeomKey = String(selectedGeom);
      const sepIdx = selectedGeomKey.indexOf(":");
      const bodyUuid = sepIdx > 0 ? selectedGeomKey.slice(0, sepIdx) : null;
      const geomUuid = sepIdx > 0 ? selectedGeomKey.slice(sepIdx + 1) : selectedGeomKey;
      const body = bodyUuid ? safeBodies[bodyUuid] : null;
      const geom = Array.isArray(body?.geoms)
        ? body.geoms.find((candidate) => String(candidate?.uuid || "") === geomUuid)
        : null;
      if ((collisionGeomVisibility[selectedGeomKey] ?? true) !== false) {
        if (geom?.visualOnly === true && !showVisual) {
          return {
            message: "Visual visible while selected.",
            visibilityLabel: "Visual",
            isVisibilityOn: () => getViewerState().showVisual,
            turnVisibilityOn: () => getViewerActions().toggleShowVisual(),
          };
        }
        if (geom && geom.visualOnly !== true && !showCollision) {
          return {
            message: "Collision visible while selected.",
            visibilityLabel: "Collision",
            isVisibilityOn: () => getViewerState().showCollision,
            turnVisibilityOn: () => getViewerActions().toggleShowCollision(),
          };
        }
      }
    }
    if (
      selectedEquality &&
      !showConstraintMarkers &&
      (equalityMarkerVisibility[selectedEquality] ?? true) !== false
    ) {
      return {
        message: "Constraint visible while selected.",
        visibilityLabel: "Constraint",
        isVisibilityOn: () => getViewerState().showConstraintMarkers,
        turnVisibilityOn: () => getViewerActions().toggleShowConstraintMarkers(),
      };
    }
    if (
      selectedTendon &&
      !showConstraintMarkers &&
      (tendonMarkerVisibility[selectedTendon] ?? true) !== false
    ) {
      return {
        message: "Tendon visible while selected.",
        visibilityLabel: "Tendon",
        isVisibilityOn: () => getViewerState().showConstraintMarkers,
        turnVisibilityOn: () => getViewerActions().toggleShowConstraintMarkers(),
      };
    }
    return null;
  }, [
    selectedJoint,
    selectedSite,
    selectedGeom,
    selectedInertial,
    selectedEquality,
    selectedTendon,
    showJoints,
    showSites,
    showVisual,
    showCollision,
    showInertials,
    showConstraintMarkers,
    jointVizVisibility,
    siteVisibility,
    collisionGeomVisibility,
    inertialVisibility,
    equalityMarkerVisibility,
    tendonMarkerVisibility,
    safeBodies,
  ]);

  const handleTurnVisibilityOn = useCallback(() => {
    if (!notice) return;
    if (notice.isVisibilityOn()) return;
    notice.turnVisibilityOn();
  }, [notice]);

  return { notice, handleTurnVisibilityOn };
}
